// src/pages/Dashboard.tsx - UPDATED WITH SEPARATE LOADING STATES
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { DashboardLayout } from '../components/layout'
import { 
  DashboardOverview, 
  OrdersSection, 
  PaymentsSection, 
  ProfileSection, 
  SettingsSection,
  EarningsTab 
} from '../components/dashboard'
import HowToUseSection from '../components/dashboard/HowToUseSection'
import { apiService, DashboardStatsResponse } from '../services/api'
import AffiliateSection from '../components/dashboard/AffiliateSection'
import PartnersSection from '../components/dashboard/PartnersSection'
import NotificationsSection from '../components/dashboard/NotificationsSection'


interface DashboardProps {
  onLogout?: () => void;
}

export default function Dashboard({ onLogout }: DashboardProps) {
  // Aktif sekme ve (orders için) sipariş id artık URL'den okunur (earnbackhub.com/orders, /orders/:id ...)
  const navigate = useNavigate()
  const location = useLocation()
  const KNOWN_TABS = ['dashboard', 'orders', 'payments', 'earnings', 'affiliate', 'partners', 'profile', 'settings', 'how-to-use', 'notifications']
  const segments = location.pathname.split('/').filter(Boolean)
  const seg0 = segments[0] || 'dashboard'
  const activeTab = KNOWN_TABS.includes(seg0) ? seg0 : 'dashboard'
  const routeOrderId = activeTab === 'orders' ? (segments[1] ? decodeURIComponent(segments[1]) : null) : null
  const setActiveTab = (tab: string) => navigate(tab === 'dashboard' ? '/' : `/${tab}`)
  const [chartType, setChartType] = useState<'line' | 'bar' | 'area'>('area')
  const [isUpdating, setIsUpdating] = useState(false)
  const [isChartRefreshing, setIsChartRefreshing] = useState(false) // YENİ: Grafik yenilemesi için ayrı state
  const [selectedDateRange, setSelectedDateRange] = useState('Son 30 gün')
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string } | null>(null)
  const [dashboardData, setDashboardData] = useState<DashboardStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)

  // Tarihleri hesapla
  const calculateDateRange = (range: string): { start: string; end: string } => {
    const today = new Date()
    let start = new Date()
    
    switch(range) {
      case 'Son 7 gün':
        start.setDate(today.getDate() - 7)
        break
      case 'Son 30 gün':
        start.setDate(today.getDate() - 30)
        break
      case 'Son 3 ay':
        start.setMonth(today.getMonth() - 3)
        break
      case 'Son 6 ay':
        start.setMonth(today.getMonth() - 6)
        break
      case 'Son 1 yıl':
        start.setFullYear(today.getFullYear() - 1)
        break
      case 'Tüm zamanlar':
        start = new Date('2020-01-01')
        break
      default:
        start.setMonth(today.getMonth() - 6)
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    }
  }

  // Backend'den dashboard verilerini çek
  const fetchDashboardStats = async (
    showLoading: boolean = true, 
    dateRange?: { start: string; end: string },
    isChartOnly: boolean = false // YENİ: Sadece grafik güncellemesi mi?
  ) => {
    if (showLoading) setLoading(true)
    else if (isChartOnly) setIsChartRefreshing(true) // YENİ: Grafik için ayrı loading
    else setIsUpdating(true)

    try {
      let rawData: any // API'den gelen raw data
      
      if (dateRange) {
        // Tarih aralığı ile veri çek (format=events ile)
        rawData = await apiService.getDashboardStats(dateRange.start, dateRange.end, 'events')
      } else {
        // Varsayılan tarih aralığı
        const defaultRange = calculateDateRange(selectedDateRange)
        rawData = await apiService.getDashboardStats(defaultRange.start, defaultRange.end, 'events')
      }
      
      // API'den gelen chartData'yı component'in beklediği formata çevir
      // ÖNCELİKLE: Aynı tarihteki siparişleri grupla
      const groupedByDate: { [key: string]: { orders: number; amount: number; bonus: number } } = {}

      rawData.chartData.forEach((item: any) => {
        try {
          if (item.ts) {
            const datePart = item.ts.split('T')[0] // "2025-10-09"
            
            if (!groupedByDate[datePart]) {
              groupedByDate[datePart] = {
                orders: 0,
                amount: 0,
                bonus: 0
              }
            }
            
            groupedByDate[datePart].orders += item.orders || 0
            groupedByDate[datePart].amount += item.amount || 0
            groupedByDate[datePart].bonus += item.bonus || 0
          }
        } catch (error) {
          console.error('Date parsing error for item:', item, error)
        }
      })

      // ADIM 2: Seçilen tarih aralığındaki TÜM günleri oluştur
      const fillDateRange = (startDate: string, endDate: string) => {
        const dates = []
        const current = new Date(startDate)
        const end = new Date(endDate)
        
        while (current <= end) {
          const dateKey = current.toISOString().split('T')[0] // "2025-10-09"
          dates.push(dateKey)
          current.setDate(current.getDate() + 1)
        }
        
        return dates
      }

      // ADIM 3: Tarih aralığı belirle
      const startDate = dateRange?.start || calculateDateRange(selectedDateRange).start
      const endDate = dateRange?.end || calculateDateRange(selectedDateRange).end

      // ADIM 4: Tüm tarihleri doldur
      const allDates = fillDateRange(startDate, endDate)

      // ADIM 5: Her tarih için veri oluştur (yoksa 0)
      const transformedChartData = allDates.map(dateKey => {
        const [year, month, day] = dateKey.split('-')
        const displayDate = `${day}.${month}`
        
        // Bu tarihte veri varsa onu kullan, yoksa 0
        const data = groupedByDate[dateKey] || { orders: 0, amount: 0, bonus: 0 }
        
        return {
          name: displayDate,
          siparisler: data.orders,
          kazanc: data.amount,
          bonus: data.bonus
        }
      })

      // Debug: Dönüşümü kontrol et
      console.log('📊 Chart Data Transformation:')
      console.log('Raw data count:', rawData.chartData.length)
      console.log('Grouped data count:', transformedChartData.length)
      console.log('Sample transformed data:', transformedChartData.slice(0, 3))

      // Transformed data'yı state'e kaydet
      const data: DashboardStatsResponse = {
        stats: rawData.stats,
        chartData: transformedChartData,
        pieData: rawData.pieData,
        recentOrders: rawData.recentOrders,
        lastOrderUpdateAt: rawData.lastOrderUpdateAt  // ✅ YENİ
      }
      
     setDashboardData(data)
      
      if (!showLoading && !isChartOnly) {
        toast.success('Dashboard verileri güncellendi!')
      } else if (isChartOnly) {
        toast.success('Grafik verileri güncellendi!') // YENİ EKLEME
      }
    } catch (error) {
      console.error('Dashboard stats error:', error)
      toast.error('Dashboard verileri yüklenirken hata oluştu')
      
      // Hata durumunda varsayılan değerler
      setDashboardData({
        stats: {
          totalOrders: 0,
          deliveredOrders: 0,
          pendingOrders: 0,
          inTransitOrders: 0,
          cancelledOrders: 0,
          totalEarnings: 0,
          totalOrderAmount: 0,
          monthlyEarnings: 0,
          bonusLevel: 1
        },
        chartData: [],
        pieData: [],
        recentOrders: [],
        lastOrderUpdateAt: null  // ✅ YENİ
      })
    } finally {
      setLoading(false)
      setIsUpdating(false)
      setIsChartRefreshing(false) // YENİ: Her durumda chart refreshing'i kapat
    }
  }

  useEffect(() => {
    // İlk yüklemede varsayılan tarih aralığı ile çek
    const defaultRange = calculateDateRange(selectedDateRange)
    fetchDashboardStats(true, defaultRange, false)
  }, [])

  // Event handlers
  const handleUpdateOrders = async () => {
    const currentRange = customDateRange || calculateDateRange(selectedDateRange)
    await fetchDashboardStats(false, currentRange, false) // Sipariş güncellemesi
  }

const handleChartUpdate = () => {
    const currentRange = customDateRange || calculateDateRange(selectedDateRange)
    fetchDashboardStats(false, currentRange, true)
  }

  const handleLogout = () => {
    toast.success('Başarıyla çıkış yaptınız!')
    setTimeout(() => {
      onLogout?.()
    }, 1000)
  }

  const handleCreateOrder = () => {
    toast.success('Sipariş oluşturma sayfası açılıyor...')
  }

  const handleViewAllOrders = () => {
    setActiveTab('orders')
  }

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const newRange = { start: startDate, end: endDate }
    setCustomDateRange(newRange)
    
    // Tarih aralığını güzel bir formata çevir
    const start = new Date(startDate)
    const end = new Date(endDate)
    const rangeText = `${start.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`
    
    setSelectedDateRange(rangeText)
    
    // Yeni tarih aralığı ile veri çek
    fetchDashboardStats(false, newRange, true) // YENİ: Sadece grafik güncellemesi
    
    toast.success(`${rangeText} aralığı seçildi`)
  }

  const handlePresetDateRangeChange = (range: string) => {
    setSelectedDateRange(range)
    setCustomDateRange(null)
    
    // Preset aralık için tarihleri hesapla ve veri çek
    const dateRange = calculateDateRange(range)
    fetchDashboardStats(false, dateRange, true) // YENİ: Sadece grafik güncellemesi
  }

  // Render content based on active tab
  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600">Dashboard yükleniyor...</span>
          </div>
        </div>
      )
    }

    if (!dashboardData) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600">Dashboard verileri yüklenemedi</p>
        </div>
      )
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardOverview
            stats={{
              totalOrders: dashboardData.stats.totalOrders,
              deliveredOrders: dashboardData.stats.deliveredOrders,
              pendingOrders: dashboardData.stats.pendingOrders,
              totalEarnings: dashboardData.stats.totalEarnings,
              totalOrderAmount: dashboardData.stats.totalOrderAmount,
              bonusLevel: dashboardData.stats.bonusLevel,
              cancelledOrders: dashboardData.stats.cancelledOrders
            }}
            chartType={chartType}
            onChartTypeChange={setChartType}
            selectedDateRange={selectedDateRange}
            onChartUpdate={handleChartUpdate}
            onDateRangeChange={handleDateRangeChange}
            onPresetDateRangeChange={handlePresetDateRangeChange}
            isUpdating={isUpdating}
            onUpdateOrders={handleUpdateOrders}
            onViewAllOrders={handleViewAllOrders}
            chartData={dashboardData.chartData}
            pieData={dashboardData.pieData}
            recentOrders={dashboardData.recentOrders}
            lastOrderUpdateAt={dashboardData.lastOrderUpdateAt}  // ✅ YENİ
          />
        )
      case 'orders':
        return <OrdersSection routeOrderId={routeOrderId} />
      case 'payments':
        return <PaymentsSection />
      case 'earnings':
        return <EarningsTab />
        case 'affiliate':
          return <AffiliateSection />
      case 'partners':
        return <PartnersSection />
      case 'profile':
        return (
          <ProfileSection 
            stats={{
              bonusLevel: dashboardData.stats.bonusLevel,
              totalOrders: dashboardData.stats.totalOrders,
              totalEarnings: dashboardData.stats.totalEarnings
            }} 
          />
        )
      case 'settings':
        return <SettingsSection />
      case 'how-to-use':
        return <HowToUseSection />
      case 'notifications':
        return <NotificationsSection />
      default:
        return null
    }
  }

  return (
    <DashboardLayout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onLogout={handleLogout}
      onCreateOrder={handleCreateOrder}
      stats={{
        bonusLevel: dashboardData?.stats.bonusLevel || 1
      }}
    >
      {renderTabContent()}
    </DashboardLayout>
  )
}